import qs from 'qs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createStyles, Flex } from '@mantine/core';
import { useRouter } from 'next/router';
import { IconLock } from '@tabler/icons-react';

import { Text } from '~/domains/design-system/Text';

import type { StaticFieldProps } from '../../types/common';
import { getFilteredSelectOptions } from '../utils/getFilteredSelectOptions';
import { FieldMeta } from '../common/FieldMeta';
import SelectFieldOptions from '../common/SelectFieldOptions';
import autoScrollToNextField from '../utils/autoScrollToNextField';
import { useSportStyleChangeHandler } from './useSportStyleChangeHandler';

import type { SingleSelectField } from './types';

const useStyles = createStyles((theme) => ({
  container: {
    position: 'relative',
  },
  lockedLabelWrapper: {
    position: 'relative',
    cursor: 'default',
  },
  lockedLabel: {
    paddingBottom: theme.other.spacing._8,
  },
  lockIcon: {
    color: theme.colors.gray2[6],
  },
  tooltip: {
    position: 'absolute',
    left: 0,
    top: '100%',
    zIndex: 10,
  },
  tooltipLabel: {
    color: theme.colors.gray2[9],
    paddingTop: theme.other.spacing._8,
    paddingBottom: theme.other.spacing._8,
    paddingLeft: theme.other.spacing._16,
    paddingRight: theme.other.spacing._16,
    background: theme.colors.gray2[0],
    border: `1px solid ${theme.colors.gray2[2]}`,
    borderRadius: theme.other.spacing._4,
    whiteSpace: 'nowrap',
  },
}));

export function SingleSelectFieldElement({
  field,
  path,
  propagateFieldLabelToParent,
  value,
  onValueChange,
  errors,
  isTouched,
  sectionKey,
  allValues,
  onGoToSection,
  onNextSectionClick,
  allErrors,
}: StaticFieldProps<SingleSelectField>) {
  const ref = useRef<HTMLDivElement>();
  const { classes } = useStyles();
  const router = useRouter();
  const [hover, setHover] = useState(false);
  const currentQuery = router.query;

  const { handleSportStyleChange } = useSportStyleChangeHandler({
    sectionKey,
    fieldKey: field.key,
    currentValue: value as string,
  });
  const initialValues = useMemo<
    Partial<{ stepOne: { leagueId: string; contestTemplateId: string } }>
  >(() => qs.parse(qs.stringify(router.query, { encode: false })), [router.query]);

  const options = getFilteredSelectOptions({
    options: field.options,
    sectionKey,
    allValues,
    allErrors,
  });

  useEffect(() => {
    if (initialValues?.[sectionKey]?.[field.key]) {
      autoScrollToNextField(field?.scrollToNextSection, ref?.current);
    }
  }, [field.key, field?.scrollToNextSection, initialValues, sectionKey]);

  const handleOnChange = useCallback(
    (e) => {
      const { target } = e;
      const targetOption = options.find((option) => `${option.value}` === `${target.value}`);
      if (!targetOption) {
        return;
      }

      const isIntercepted = handleSportStyleChange(targetOption.value as string);
      if (isIntercepted) {
        return;
      }

      if (field?.autoFillSteps) {
        currentQuery.status = 'inprogress';

        router.push({
          pathname: router.pathname,
          query: currentQuery,
        });
        onNextSectionClick();
      }

      if (field?.goToNextSection) {
        onGoToSection(field?.goToNextSection);
        return;
      }

      autoScrollToNextField(field?.scrollToNextSection, ref?.current);
      onValueChange(targetOption.value);
    },
    [
      options,
      field?.autoFillSteps,
      field?.goToNextSection,
      field?.scrollToNextSection,
      onValueChange,
      currentQuery,
      router,
      onNextSectionClick,
      onGoToSection,
      handleSportStyleChange,
    ]
  );

  useEffect(() => {
    if (!value && !field?.defaultValue) return;
    if (options.find((option) => option.value === value)) return;
    if (field?.defaultValue === value) return;

    onValueChange(field?.defaultValue || null, true);
  }, [field?.defaultValue, onValueChange, options, value]);

  const displayValue = (field?.locked ? (value ?? field?.defaultValue) : value) as string;

  const isPropagateFieldLabelToParent = typeof propagateFieldLabelToParent === 'function';
  useEffect(() => {
    if (!isPropagateFieldLabelToParent) return;

    const selectedOption = options.find((option) => option.value === displayValue);
    if (typeof selectedOption?.overrideLabel === 'string') {
      propagateFieldLabelToParent(
        path,
        selectedOption.doNotPropagate ? undefined : selectedOption.overrideLabel
      );
    } else if (typeof selectedOption?.label === 'string') {
      propagateFieldLabelToParent(
        path,
        selectedOption.doNotPropagate ? undefined : selectedOption.label
      );
    }

    return () => {
      propagateFieldLabelToParent(path, undefined);
    };
  }, [isPropagateFieldLabelToParent, displayValue]);

  if (options.length === 0) {
    return null;
  }

  const isLocked = !!field.locked;

  return (
    <div className={classes.container} ref={ref}>
      {isLocked && field.label && (
        <div
          className={classes.lockedLabelWrapper}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
        >
          <div className={classes.lockedLabel}>
            <Flex align="center" /* eslint-disable-line local-rules/use-theme-tokens */ gap={4}>
              <Text c="gray2.5" variant="subhead-small" transform="uppercase">
                {field.label} {field?.isRequired && '*'}
              </Text>
              <IconLock size={16} className={classes.lockIcon} />
            </Flex>
          </div>
          {hover && (
            <div className={classes.tooltip}>
              <Text className={classes.tooltipLabel} variant="body-medium">
                {field.locked}
              </Text>
            </div>
          )}
        </div>
      )}
      <FieldMeta
        info={field?.info}
        field={field}
        errors={errors}
        isTouched={isTouched}
        hideLabel={isLocked}
      />
      <SelectFieldOptions
        field={field}
        options={options}
        value={displayValue}
        onChange={handleOnChange}
      />
    </div>
  );
}
